import type { LosType, UserRole, WizardState } from '../pages/lenders/wizard/types';
import type { Product } from './products';
import apiClient from './client';

/**
 * The wizard's boundary with the backend. Every function here is a real call now; the submit
 * became real on 2026-08-28.
 */

export interface OnboardingPayload {
  account: {
    lenderName: string;
    lenderId: string;
    salesforceId: string;
    entityId: string;
    entityName: string;
    apiUsername: string;
    apiPassword: string;
  };
  losIntegrations: { type: LosType; credentials: Record<string, string> }[];
  products: { name: string; urn: string }[];
  users: {
    firstName: string;
    lastName: string;
    mlConsumerUserName: string;
    mlMortgageUserName: string;
    email: string;
    role: UserRole | '';
    productAccess: Record<string, boolean> | null;
  }[];
}

/**
 * `catalog` is here because the wizard state never carried product names — it stores
 * `Record<urn, boolean>` — while the lender record stores `{ name, urn }` per product. The
 * catalog the caller already holds (Step 5 renders the tree from it) resolves each enabled urn
 * to its vendor product name.
 */
/**
 * Leading and trailing spaces typed into the wizard were being stored verbatim (OM-4602).
 *
 * This is now the BACKSTOP, not the fix. Trimming only here left the padded value in form state,
 * where the anchored rules still failed on it — a trailing space made Email read "Invalid format"
 * rather than coming off — so QA rejected it. The trim that the operator sees lives on each
 * field's blur handler instead (see `wizard/trimOnBlur.ts`).
 *
 * Kept because not every value reaches the payload through a blur: Step 2's credentials can be
 * cleared programmatically when an instance id turns out to be taken, and a field left untouched
 * since a draft was restored never fires one.
 */
const trimmed = (value: string): string => value.trim();

/** Every string property of an object, trimmed; anything else is passed through untouched. */
const trimValues = <T extends object>(values: T): T =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, typeof value === 'string' ? trimmed(value) : value]),
  ) as T;

export function buildOnboardingPayload(state: WizardState, catalog: Product[]): OnboardingPayload {
  const names = new Map<string, string>();
  for (const product of catalog) {
    for (const vp of product.vendorProducts ?? []) {
      names.set(vp.urn, vp.vendorProductName || product.productName);
    }
  }

  const enabledProducts = Object.entries(state.products)
    .filter(([, enabled]) => enabled)
    .map(([urn]) => urn);
  const enabledProductUrns = new Set(enabledProducts);

  return {
    // Every account value is operator-typed text, so every one of them goes through the trim.
    account: trimValues(state.account),
    losIntegrations: state.losIntegrations.map((los) => ({
      type: los.type,
      credentials: trimValues(los.credentials),
    })),
    products: enabledProducts.map((urn) => ({ urn, name: names.get(urn) ?? urn })),
    users: state.users.map((u) => ({
      firstName: trimmed(u.firstName),
      lastName: trimmed(u.lastName),
      mlConsumerUserName: trimmed(u.mlConsumerUserName),
      mlMortgageUserName: trimmed(u.mlMortgageUserName),
      email: trimmed(u.email),
      // Not trimmed: a fixed enum, not free-form text.
      role: u.role,
      // A user's restricted access can go stale: if the lender's enabled product set shrinks in
      // Step 3 after the user was granted access, the reducer never prunes the old keys out of
      // productAccess. Intersect with the still-enabled set here so a disabled product's key
      // never reaches the payload. null stays null — it means "inherits everything" and must not
      // collapse into {}, which would mean "access to nothing".
      productAccess: u.productAccess
        ? Object.fromEntries(
            Object.entries(u.productAccess).filter(([urn]) => enabledProductUrns.has(urn)),
          )
        : null,
    })),
  };
}

/**
 * Real call, unlike the rest of this module... which is all real now too. The wizard asks on
 * every keystroke that produces a well-formed id, so a failure here resolves to "free": a check
 * that cannot run must not be what stops someone from creating a lender. A genuine collision is
 * caught again on submit.
 */
export async function checkSalesforceIdUnique(id: string): Promise<boolean> {
  try {
    const response = await apiClient.get(`/oms/lenders/salesforce-id/${encodeURIComponent(id)}`);
    return response.data.available;
  } catch {
    return true;
  }
}

/** The backend's LOS path segment for each ML side of Step 2. */
const ML_CODE_PATH: Record<'ml-consumer' | 'ml-mortgage', string> = {
  'ml-consumer': 'meridianlink',
  'ml-mortgage': 'meridianlink-mortgage',
};

/**
 * Whether an ML Lender Code is still free (OM-3918: unique per lender). Same failure stance
 * as the other probes, and the holder travels back so the inline error can name it.
 */
export async function checkMlLenderCodeAvailable(
  los: 'ml-consumer' | 'ml-mortgage',
  code: string,
): Promise<{ available: boolean; lenderName?: string }> {
  try {
    const response = await apiClient.get(
      `/oms/lenders/lender-code/${ML_CODE_PATH[los]}/${encodeURIComponent(code)}`,
    );
    const { available, lender } = response.data;
    return { available, lenderName: lender?.displayName || lender?.name };
  } catch {
    return { available: true };
  }
}

/**
 * Same failure stance as the Salesforce id check above. `lender` scopes the check to the
 * account being created (OM-3920 AC9); without it the backend checks system-wide.
 */
export async function checkUserEmailUnique(email: string, lender?: string): Promise<boolean> {
  try {
    const scope = lender ? `?lender=${encodeURIComponent(lender)}` : '';
    const response = await apiClient.get(`/oms/users/email/${encodeURIComponent(email)}${scope}`);
    return response.data.available;
  } catch {
    return true;
  }
}

/**
 * Whether the generated account identifier is free, i.e. no lender already carries it as its
 * name. Same failure stance as the other probes.
 */
export async function checkLenderIdAvailable(lenderId: string): Promise<{ available: boolean }> {
  try {
    const response = await apiClient.get(`/oms/lenders/lender-id/${encodeURIComponent(lenderId)}`);
    return { available: response.data.available };
  } catch {
    return { available: true };
  }
}

/**
 * Whether an Encompass instance id already belongs to a lender (OM-3918 AC5). The holder's
 * name comes back with the verdict so the dialog can identify it. Failure resolves to
 * available, like the other probes: a check that cannot run must not block creation.
 */
export async function checkInstanceIdAvailable(
  instanceId: string,
): Promise<{ available: boolean; lenderName?: string }> {
  try {
    const response = await apiClient.get(
      `/oms/lenders/instance-id/${encodeURIComponent(instanceId)}`,
    );
    const { available, lender } = response.data;
    return { available, lenderName: lender?.displayName || lender?.name };
  } catch {
    return { available: true };
  }
}

/** The wizard collects one id per Encompass service; each maps to a serviceSetup entry. */
const ENCOMPASS_SERVICE_TYPES: [credentialKey: string, serviceType: string][] = [
  ['floodServiceId', 'Flood Order'],
  ['titleServiceId', 'Title Order'],
  ['valuationServiceId', 'Valuation Order'],
];

/**
 * TECHNICAL DEBT — the submit is NOT atomic, by decision, not oversight.
 *
 * It is a sequence of writes no transaction can span: the Encompass service configuration
 * through the /los corridor first (an upsert by instance id — retrying is harmless), then the
 * membership aggregate (lender, credentials, products, users), which is itself create-or-continue
 * per piece. MeridianLink Consumer and Mortgage share one API, so at most two external systems
 * can fail independently of each other.
 *
 * Nothing in the stack offers a saga, outbox, or compensating rollback, and building one for an
 * operation this infrequent is not worth it. The ORDER above puts the likelier-orphaned write
 * first, and a failure anywhere leaves the operator on Step 5 with everything preserved — retry
 * resumes rather than duplicating.
 */
export async function submitLenderOnboarding(
  payload: OnboardingPayload,
): Promise<{ urn: string }> {
  try {
    const encompass = payload.losIntegrations.find((los) => los.type === 'encompass');
    if (encompass) {
      const { instanceId } = encompass.credentials;
      await apiClient.put('/los/encompass/services', {
        // The services doc keys the lender by its urn, `urn:lender:<account id>`, as every
        // collection linked to the lender does.
        lenderId: `urn:lender:${payload.account.lenderId}`,
        lenderName: payload.account.lenderName,
        encompassConfiguration: {
          instanceId,
          serviceSetup: ENCOMPASS_SERVICE_TYPES.filter(([key]) => encompass.credentials[key]).map(
            ([key, serviceType]) => ({
              // Only the field this write owns. The proxy merges by serviceType and preserves
              // any stored vendorGatewayServiceId — real documents carry values there that
              // differ from the setup id, with a provenance of their own.
              encompassServiceSetupId: encompass.credentials[key],
              serviceType,
            }),
          ),
        },
      });
    }

    const response = await apiClient.post('/oms/lenders/onboarding', payload);
    return response.data;
  } catch (error) {
    // Surface the backend's own message when it sent one (a 422 explains itself); the ticket's
    // copy is the fallback for failures that carry none.
    const message = (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message;
    throw new Error(message || 'Unable to create the lender account. Please try again.');
  }
}
